/**
 * Key combinations (design/ux/01_foundations.md §4.2-§4.3, 02_UX_DESIGN §2).
 *
 * One notation for every binding of the keyboard layer, so the dispatcher
 * that fires a shortcut, the F1 sheet that documents it and the ActionBar
 * chip that advertises it read the SAME value and cannot drift:
 *
 *   "F9" · "Escape" · "Delete" · "Enter" · "Ctrl+S" · "Ctrl+Shift+A" ·
 *   "Alt+M" · "Alt+Digit3" (matched on KeyboardEvent.code — AZERTY) ·
 *   "Ctrl+ArrowUp" · "Shift+F10" · "Plus" / "Minus" (the + and − keys)
 *
 * Modifiers are EXACT (§4.3 rule 1): "Ctrl+S" needs ctrlKey and neither Alt
 * nor Meta. AltGr arrives as Ctrl+Alt on AZERTY and never fires anything
 * (no binding may use Ctrl+Alt). Shift is ignored for the symbol keys
 * (+ − and friends), whose Shift state depends on the layout.
 *
 * Pure module (no DOM, no config import) — the config layer derives its
 * display strings from it.
 */

const MODIFIERS = ["Ctrl", "Alt", "Shift"];

/** French display names (UI copy — 02_UX_DESIGN §5.2 terminology). */
const KEY_LABELS = Object.freeze({
  Escape: "Échap",
  Enter: "Entrée",
  Delete: "Suppr",
  Backspace: "Retour arrière",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  PageUp: "Pg préc.",
  PageDown: "Pg suiv.",
  Home: "Début",
  End: "Fin",
  Space: "Espace",
  Tab: "Tab",
  ContextMenu: "Menu contextuel",
  Plus: "+",
  Minus: "−",
});
const MODIFIER_LABELS = Object.freeze({ Ctrl: "Ctrl", Alt: "Alt", Shift: "Maj" });

/** Keys whose Shift state is layout-dependent (never part of the match). */
const SYMBOL_KEYS = Object.freeze({ Plus: ["+"], Minus: ["-", "−"] });

/**
 * @typedef {Object} Combo
 * @property {boolean} ctrl
 * @property {boolean} alt
 * @property {boolean} shift
 * @property {string}  key   canonical key token ("F9", "K", "Escape", "Digit3", "Plus")
 * @property {"key"|"code"|"symbol"|"letter"} match
 */

/**
 * @param {string} spec e.g. "Ctrl+Shift+A"
 * @returns {Combo}
 */
export function parseCombo(spec) {
  const raw = String(spec ?? "").trim();
  if (!raw) throw new Error("parseCombo: empty combination");
  const parts = raw.split("+").map((p) => p.trim());
  if (parts.some((p) => !p)) throw new Error(`parseCombo: malformed « ${raw} » (write Plus for the + key)`);
  const key = parts.pop();
  const mods = new Set();
  for (const m of parts) {
    if (!MODIFIERS.includes(m)) throw new Error(`parseCombo: unknown modifier « ${m} » in « ${raw} »`);
    if (mods.has(m)) throw new Error(`parseCombo: duplicate modifier in « ${raw} »`);
    mods.add(m);
  }
  let match = "key";
  if (/^Digit[0-9]$/.test(key)) match = "code";
  else if (SYMBOL_KEYS[key]) match = "symbol";
  else if (/^[A-Z]$/.test(key)) match = "letter";
  else if (!/^(F([1-9]|1[0-2])|Escape|Enter|Delete|Backspace|Arrow(Up|Down|Left|Right)|Page(Up|Down)|Home|End|Space|Tab|ContextMenu)$/.test(key)) {
    throw new Error(`parseCombo: unknown key « ${key} » in « ${raw} »`);
  }
  return Object.freeze({
    ctrl: mods.has("Ctrl"),
    alt: mods.has("Alt"),
    shift: mods.has("Shift"),
    key,
    match,
  });
}

/** Canonical string of a combo (modifier order Ctrl, Alt, Shift). */
export function comboId(spec) {
  const c = typeof spec === "string" ? parseCombo(spec) : spec;
  return [c.ctrl && "Ctrl", c.alt && "Alt", c.shift && "Shift", c.key].filter(Boolean).join("+");
}

/**
 * French display of a combo: "Ctrl + Maj + A", "Échap", "Alt + 3".
 * @param {string|Combo} spec
 */
export function formatCombo(spec) {
  const c = typeof spec === "string" ? parseCombo(spec) : spec;
  const mods = MODIFIERS.filter((m) => c[m.toLowerCase()]).map((m) => MODIFIER_LABELS[m]);
  let key = KEY_LABELS[c.key] ?? c.key;
  if (c.match === "code") key = c.key.slice(-1);
  return [...mods, key].join(" + ");
}

/** True when the event is AltGr (a character key on AZERTY, never a shortcut). */
export function isAltGraph(e) {
  if (typeof e?.getModifierState === "function" && e.getModifierState("AltGraph")) return true;
  return Boolean(e?.ctrlKey && e?.altKey);
}

/**
 * Does the keyboard event match the combo, with EXACT modifiers?
 * @param {{key?: string, code?: string, ctrlKey?: boolean, altKey?: boolean, shiftKey?: boolean, metaKey?: boolean}} e
 * @param {string|Combo} spec
 */
export function matchCombo(e, spec) {
  if (!e) return false;
  const c = typeof spec === "string" ? parseCombo(spec) : spec;
  if (e.metaKey) return false;
  if (isAltGraph(e)) return false;
  if (Boolean(e.ctrlKey) !== c.ctrl || Boolean(e.altKey) !== c.alt) return false;
  if (c.match !== "symbol" && Boolean(e.shiftKey) !== c.shift) return false;
  switch (c.match) {
    case "code":
      return e.code === c.key;
    case "symbol":
      return SYMBOL_KEYS[c.key].includes(e.key) || e.code === (c.key === "Plus" ? "NumpadAdd" : "NumpadSubtract");
    case "letter": {
      const k = String(e.key ?? "");
      if (k.length === 1 && /[a-z]/i.test(k)) return k.toUpperCase() === c.key;
      // Alt+letter on some systems yields a special character: fall back to
      // the physical key.
      return e.code === `Key${c.key}`;
    }
    default:
      if (c.key === "Space") return e.key === " " || e.code === "Space";
      return e.key === c.key;
  }
}

/** F1…F12 (the function keys, which fire even inside a text field). */
export function isFunctionKey(spec) {
  const c = typeof spec === "string" ? parseCombo(spec) : spec;
  return /^F([1-9]|1[0-2])$/.test(c.key);
}

/**
 * §4.3 rule 2 — inside a text field only F-keys, Échap and chords with Ctrl
 * or Alt fire; bare letters, digits, + − and Suppr are typing. The clipboard
 * and undo chords (Ctrl+C/V/X/Z/Y/A) always stay with the field.
 * @param {string|Combo} spec
 */
export function firesInTextField(spec) {
  const c = typeof spec === "string" ? parseCombo(spec) : spec;
  if (c.ctrl && !c.alt && !c.shift && c.match === "letter" && "CVXZYA".includes(c.key)) return false;
  if (isFunctionKey(c) || c.key === "Escape") return true;
  return c.ctrl || c.alt;
}

const TEXT_INPUT_TYPES = new Set([
  "",
  "text",
  "search",
  "email",
  "tel",
  "url",
  "password",
  "number",
  "date",
  "datetime-local",
  "month",
  "time",
  "week",
]);

/**
 * Is the element a place where keys are typing (input, textarea, select,
 * contenteditable)? Works on real elements and on plain test doubles.
 * @param {any} el
 */
export function isTextField(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = String(el.tagName ?? "").toUpperCase();
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag !== "INPUT") return false;
  const type = String(el.type ?? el.getAttribute?.("type") ?? "").toLowerCase();
  return TEXT_INPUT_TYPES.has(type);
}
